import { Link } from 'react-router-dom';
import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/feature/ComicCard';
import { getHotComics, getNewComics, comics } from '@/mocks/comics';
import { categories } from '@/mocks/categories';
import { reviews } from '@/mocks/reviews';

export default function Home() {
  const hotComics = getHotComics(8);
  const newComics = getNewComics(8);

  const jsonLdOrganization = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '漫画皮',
    url: 'http://qjmd.cn/',
    description: '漫画皮漫画网提供在线漫画免费阅读服务，高清好看的网络漫画尽在平台。',
    foundingDate: '2018',
    contactPoint: {
      '@type': 'ContactPoint',
      email: 'hello@qjmd.cn',
      contactType: 'customer service',
      availableLanguage: ['Chinese'],
    },
  };

  const jsonLdWebsite = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '漫画皮',
    url: 'http://qjmd.cn/',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'http://qjmd.cn/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  const jsonLdFaq = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '漫画皮上的漫画是免费的吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '是的，漫画皮平台上的所有漫画均免费提供在线阅读。',
        },
      },
      {
        '@type': 'Question',
        name: '漫画皮有哪些漫画分类？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '漫画皮拥有热血、恋爱、悬疑、奇幻、搞笑、科幻、武侠、都市、冒险等多种分类，满足不同读者的阅读需求。',
        },
      },
      {
        '@type': 'Question',
        name: '漫画皮每天更新多少部漫画？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '漫画皮平台上的热门连载作品通常保持每周更新，部分作品双周更或月更，每天都有多部漫画更新最新章节。',
        },
      },
    ],
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(jsonLdOrganization)}</script>
      <script type="application/ld+json">{JSON.stringify(jsonLdWebsite)}</script>
      <script type="application/ld+json">{JSON.stringify(jsonLdFaq)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        {/* Hero Section */}
        <section className="relative w-full h-[580px] md:h-[680px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20artistic%20abstract%20illustration%20with%20flowing%20ink%20wash%20style%20in%20amber%20coral%20and%20cream%20tones%2C%20soft%20gradient%20texture%20reminiscent%20of%20traditional%20Chinese%20painting%20combined%20with%20modern%20comic%20aesthetics%2C%20ethereal%20light%20effects%2C%20clean%20minimal%20composition%2C%20no%20text%2C%20no%20people%2C%20abstract%20artistic%20background&width=1920&height=800&seq=hero-bg-mangapi&orientation=landscape"
            alt="漫画皮 - 高清免费漫画在线阅读"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-background-50"></div>
          <div className="relative z-10 w-full h-full flex flex-col items-center justify-center text-center px-6">
            <h1 className="text-4xl md:text-6xl font-extrabold text-background-50 mb-6 leading-tight">
              高清免费漫画
              <br />
              尽在<span className="text-accent-500">漫画皮</span>
            </h1>
            <p className="text-lg md:text-xl text-background-50/80 mb-8 max-w-xl">
              每日更新 · 正版高清 · 沉浸阅读 —— 发现你的下一个最爱
            </p>
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <Link
                to="/category/all"
                className="px-8 py-3.5 bg-accent-500 text-background-50 rounded-full text-base font-semibold hover:bg-accent-600 transition-all whitespace-nowrap"
              >
                立即阅读
                <i className="ri-arrow-right-line ml-2"></i>
              </Link>
              <Link
                to="/about"
                className="px-8 py-3.5 bg-background-50/10 backdrop-blur-sm text-background-50 border border-background-50/30 rounded-full text-base font-medium hover:bg-background-50/20 transition-all whitespace-nowrap"
              >
                了解更多
              </Link>
            </div>
            <div className="flex items-center gap-8 mt-12 text-background-50/70 text-sm">
              <div className="text-center">
                <div className="text-2xl font-bold text-background-50">12,000+</div>
                <div>漫画作品</div>
              </div>
              <div className="w-px h-10 bg-background-50/20"></div>
              <div className="text-center">
                <div className="text-2xl font-bold text-background-50">8,500万+</div>
                <div>忠实读者</div>
              </div>
              <div className="w-px h-10 bg-background-50/20"></div>
              <div className="text-center">
                <div className="text-2xl font-bold text-background-50">500,000+</div>
                <div>漫画章节</div>
              </div>
            </div>
          </div>
        </section>

        {/* Hot Comics Horizontal Scroll */}
        <section className="py-14 md:py-18 bg-background-100">
          <div className="w-full max-w-[1280px] mx-auto px-6 md:px-10">
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-3">
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-950">
                  热门<span className="text-accent-500">连载</span>
                </h2>
                <span className="px-3 py-1 text-xs font-medium bg-accent-100 text-accent-700 rounded-full whitespace-nowrap">
                  实时热度
                </span>
              </div>
              <Link
                to="/category/all"
                className="text-sm text-foreground-500 hover:text-accent-500 transition-colors flex items-center gap-1 whitespace-nowrap"
              >
                查看全部
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="flex gap-5 overflow-x-auto pb-4 scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
              {hotComics.map((comic) => (
                <div key={comic.id} className="flex-shrink-0 w-[180px] md:w-[200px]">
                  <ComicCard comic={comic} />
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Category Grid */}
        <section className="py-14 md:py-18 bg-background-50">
          <div className="w-full max-w-[1280px] mx-auto px-6 md:px-10">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950">
                分类<span className="text-accent-500">精选</span>
              </h2>
            </div>
            <div className="flex flex-wrap gap-2 mb-8">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="px-5 py-2 text-sm font-medium rounded-full border border-background-200/70 text-foreground-600 hover:border-accent-500 hover:text-accent-500 hover:bg-accent-50 transition-all whitespace-nowrap"
                >
                  {cat.name}
                </Link>
              ))}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-5">
              {newComics.map((comic) => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>
          </div>
        </section>

        {/* Reviews */}
        <section className="py-14 md:py-18 bg-background-100">
          <div className="w-full max-w-[1280px] mx-auto px-6 md:px-10">
            <div className="mb-8">
              <span className="inline-block px-3 py-1 text-xs font-medium bg-secondary-100 text-secondary-700 rounded-full mb-3 whitespace-nowrap">
                读者原声
              </span>
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950">
                漫友<span className="text-accent-500">热评</span>
              </h2>
            </div>
            <div className="columns-1 sm:columns-2 lg:columns-3 gap-5">
              {reviews.slice(0, 6).map((review) => (
                <div key={review.id} className="break-inside-avoid mb-5 bg-background-50 rounded-lg p-5 border border-background-200/70">
                  <div className="flex items-center gap-3 mb-3">
                    <img
                      src={review.avatar}
                      alt={review.userName}
                      className="w-10 h-10 rounded-full object-cover"
                      loading="lazy"
                    />
                    <div>
                      <div className="text-sm font-semibold text-foreground-950 whitespace-nowrap">{review.userName}</div>
                      <div className="text-xs text-foreground-400">
                        <i className="ri-time-line mr-0.5"></i>
                        {review.time}
                      </div>
                    </div>
                    <div className="ml-auto flex items-center gap-0.5 text-accent-500">
                      {Array.from({ length: review.rating }).map((_, i) => (
                        <i key={i} className="ri-star-fill text-xs"></i>
                      ))}
                    </div>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    <i className="ri-double-quotes-l text-accent-300 mr-1"></i>
                    {review.content}
                    <i className="ri-double-quotes-r text-accent-300 ml-1"></i>
                  </p>
                  <div className="flex items-center gap-1 mt-3 text-xs text-foreground-400">
                    <i className="ri-thumb-up-line"></i>
                    <span>{review.likes}</span>
                    <span className="ml-2">评《{review.comicTitle}》</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 md:py-20 bg-background-50">
          <div className="w-full max-w-[1280px] mx-auto px-6 md:px-10 text-center">
            <h2 className="text-3xl md:text-4xl font-extrabold text-foreground-950 mb-4">
              追更不<span className="text-accent-500">迷路</span>
            </h2>
            <p className="text-foreground-500 mb-8 max-w-md mx-auto">
              加入漫画皮，收藏你喜爱的漫画，第一时间收到更新通知。
            </p>
            <Link
              to="/category/all"
              className="inline-flex items-center px-8 py-3.5 bg-primary-500 text-background-50 rounded-full text-base font-semibold hover:bg-primary-600 transition-all whitespace-nowrap"
            >
              开始探索
              <i className="ri-arrow-right-line ml-2"></i>
            </Link>
            <div className="mt-10 flex justify-center gap-3 flex-wrap">
              {comics.slice(0, 6).map((comic) => (
                <Link
                  key={comic.id}
                  to={`/comic/${comic.id}`}
                  className="w-16 h-16 md:w-20 md:h-20 rounded-lg overflow-hidden bg-background-100 hover:ring-2 hover:ring-accent-500 transition-all"
                >
                  <img src={comic.cover} alt={comic.title} className="w-full h-full object-cover" loading="lazy" />
                </Link>
              ))}
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}